import * as React from "react";
import { Clock } from "lucide-react";
import { Button } from "./button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import { cn } from "@/lib/utils";

const TimePicker = ({ value, onChange, disabled, className }) => {
  const [open, setOpen] = React.useState(false);
  const [selectedHour, setSelectedHour] = React.useState(0);
  const [selectedMinute, setSelectedMinute] = React.useState(0);
  const [mode, setMode] = React.useState("hour"); // "hour" or "minute"
  const [isAM, setIsAM] = React.useState(true); // true for AM, false for PM
  const [hoveredHour, setHoveredHour] = React.useState(null);
  const [hoveredMinute, setHoveredMinute] = React.useState(null);

  // Parsear el valor inicial
  React.useEffect(() => {
    if (value) {
      const [hours, minutes] = value.split(":").map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        setSelectedHour(hours);
        setSelectedMinute(minutes);
        setIsAM(hours < 12);
      }
    } else {
      setSelectedHour(0);
      setSelectedMinute(0);
      setIsAM(true);
    }
  }, [value]);

  const handleHourSelect = (hour) => {
    // hour viene como 1-12 del reloj
    // Convertir a formato 24 horas basado en AM/PM
    let hour24;
    if (hour === 12) {
      hour24 = isAM ? 0 : 12; // 12 AM = 0:00, 12 PM = 12:00
    } else {
      hour24 = isAM ? hour : hour + 12; // 1-11 AM = 1-11, 1-11 PM = 13-23
    }
    setSelectedHour(hour24);
    setMode("minute");
  };

  const handleMinuteSelect = (minute) => {
    setSelectedMinute(minute);
    // Formato 24 horas para el backend
    const timeString = `${String(selectedHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    onChange(timeString);
    setOpen(false);
    setMode("hour");
  };

  const formatTime = (hours, minutes) => {
    const h = hours % 12 || 12;
    const ampm = hours < 12 ? "AM" : "PM";
    return `${String(h).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
  };

  const displayValue = value
    ? (() => {
        const [h, m] = value.split(":").map(Number);
        if (isNaN(h) || isNaN(m)) return "--:-- --";
        return formatTime(h, m);
      })()
    : "--:-- --";

  // Generar números para el reloj
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  // Calcular posición en el círculo
  const getPosition = (index, total, radius = 70) => {
    const angle = (index * 360) / total - 90; // -90 para empezar arriba
    const rad = (angle * Math.PI) / 180;
    return {
      x: radius * Math.cos(rad),
      y: radius * Math.sin(rad),
    };
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-9",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <Clock className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-6" align="start">
        <div className="flex flex-col items-center space-y-4">
          {/* Reloj circular */}
          <div className="relative w-64 h-64 flex items-center justify-center">
            <svg className="w-full h-full" viewBox="0 0 200 200">
              {/* Círculo exterior */}
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-slate-300"
              />
              
              {/* Líneas de las horas */}
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30 - 90) * (Math.PI / 180);
                const x1 = 100 + 75 * Math.cos(angle);
                const y1 = 100 + 75 * Math.sin(angle);
                const x2 = 100 + 85 * Math.cos(angle);
                const y2 = 100 + 85 * Math.sin(angle);
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-slate-300"
                  />
                );
              })}
              
              {/* Números */}
              {mode === "hour" &&
                hours.map((hour, index) => {
                  const pos = getPosition(index, 12, 70);
                  const x = 100 + pos.x;
                  const y = 100 + pos.y;
                  // Convertir hora del reloj (1-12) a formato 24h para comparación
                  let hour24;
                  if (hour === 12) {
                    hour24 = isAM ? 0 : 12;
                  } else {
                    hour24 = isAM ? hour : hour + 12;
                  }
                  
                  const isSelected = selectedHour === hour24;
                  const isHovered = hoveredHour === hour;
                  
                  return (
                    <g key={hour}>
                      {/* Círculo clickeable más grande */}
                      <circle
                        cx={x}
                        cy={y}
                        r="20"
                        fill="transparent"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleHourSelect(hour)}
                        onMouseEnter={() => setHoveredHour(hour)}
                        onMouseLeave={() => setHoveredHour(null)}
                      />
                      {/* Círculo visual */}
                      <circle
                        cx={x}
                        cy={y}
                        r="16"
                        fill={isSelected ? "#3B82F6" : isHovered ? "#93C5FD" : "none"}
                        stroke={isSelected ? "#3B82F6" : isHovered ? "#60A5FA" : "currentColor"}
                        strokeWidth={isSelected ? "2" : isHovered ? "2" : "1"}
                        className={isSelected || isHovered ? "" : "text-slate-400"}
                        style={{ pointerEvents: "none", transition: "all 0.2s" }}
                      />
                      <text
                        x={x}
                        y={y + 5}
                        textAnchor="middle"
                        fontSize="14"
                        fill={isSelected || isHovered ? "white" : "currentColor"}
                        className={isSelected || isHovered ? "" : "text-slate-600"}
                        style={{ pointerEvents: "none", fontWeight: isSelected ? "bold" : "normal" }}
                      >
                        {hour}
                      </text>
                    </g>
                  );
                })}
              
              {mode === "minute" &&
                minutes.map((minute, index) => {
                  const pos = getPosition(index, 12, 70);
                  const x = 100 + pos.x;
                  const y = 100 + pos.y;
                  const isSelected = selectedMinute === minute;
                  const isHovered = hoveredMinute === minute;
                  
                  return (
                    <g key={minute}>
                      {/* Círculo clickeable más grande */}
                      <circle
                        cx={x}
                        cy={y}
                        r="20"
                        fill="transparent"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleMinuteSelect(minute)}
                        onMouseEnter={() => setHoveredMinute(minute)}
                        onMouseLeave={() => setHoveredMinute(null)}
                      />
                      {/* Círculo visual */}
                      <circle
                        cx={x}
                        cy={y}
                        r="16"
                        fill={isSelected ? "#3B82F6" : isHovered ? "#93C5FD" : "none"}
                        stroke={isSelected ? "#3B82F6" : isHovered ? "#60A5FA" : "currentColor"}
                        strokeWidth={isSelected ? "2" : isHovered ? "2" : "1"}
                        className={isSelected || isHovered ? "" : "text-slate-400"}
                        style={{ pointerEvents: "none", transition: "all 0.2s" }}
                      />
                      <text
                        x={x}
                        y={y + 5}
                        textAnchor="middle"
                        fontSize="12"
                        fill={isSelected || isHovered ? "white" : "currentColor"}
                        className={isSelected || isHovered ? "" : "text-slate-600"}
                        style={{ pointerEvents: "none", fontWeight: isSelected ? "bold" : "normal" }}
                      >
                        {minute}
                      </text>
                    </g>
                  );
                })}
              
              {/* Centro del reloj */}
              <circle cx="100" cy="100" r="4" fill="currentColor" className="text-slate-600" />
            </svg>
          </div>
          
          {/* Indicador de modo y hora seleccionada */}
          <div className="text-center space-y-3">
            <div className="text-sm font-medium text-slate-600">
              {mode === "hour" ? "Selecciona la hora" : "Selecciona los minutos"}
            </div>
            
            {/* Selector AM/PM - Solo visible en modo hora */}
            {mode === "hour" && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant={isAM ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setIsAM(true);
                    // Actualizar la hora si ya hay una seleccionada
                    if (selectedHour >= 12 && selectedHour !== 12) {
                      setSelectedHour(selectedHour - 12);
                    } else if (selectedHour === 12) {
                      setSelectedHour(0);
                    }
                  }}
                  className={isAM ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  AM
                </Button>
                <Button
                  variant={!isAM ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setIsAM(false);
                    // Actualizar la hora si ya hay una seleccionada
                    if (selectedHour < 12 && selectedHour !== 0) {
                      setSelectedHour(selectedHour + 12);
                    } else if (selectedHour === 0) {
                      setSelectedHour(12);
                    }
                  }}
                  className={!isAM ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  PM
                </Button>
              </div>
            )}
            
            <div className="text-2xl font-bold text-slate-900">
              {formatTime(selectedHour, selectedMinute)}
            </div>
            {mode === "minute" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode("hour")}
                className="mt-2"
              >
                Cambiar hora
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { TimePicker };
